/**
 * LocalStorage wrapper that allows for typing and serialization/deserialization
 *
 * Add values in your component files like so:
 * ```
 * declare global {
 *   interface LocalStorageKeys {
 *     myKeyName: number
 *   }
 * }
 * ```
 */

import { ObservableMap, Subscription } from '@stencil/store'

/**
 * Save data in localStorage with typed key/value pairs
 */
export function setStoredValue<K extends keyof LocalStorageKeys>(key: K, data: LocalStorageKeys[K]) {
  // eslint-disable-next-line
  localStorage.setItem(key, JSON.stringify(data))
}

/**
 * Get saved data from localStorage
 */
export function getStoredValue<K extends keyof LocalStorageKeys>(
  key: K,
  defaultValue?: LocalStorageKeys[K]
): LocalStorageKeys[K] {
  // eslint-disable-next-line
  const rawValue = localStorage.getItem(key)
  try {
    const parsed = JSON.parse(rawValue)
    return typeof parsed === 'boolean' ? parsed : parsed || defaultValue
  } catch {
    return defaultValue
  }
}

/**
 * Subscribe to property changes in @stencil/store and save/restore values
 */
export function useStoredValue<T extends ObservableMap<any>, K extends keyof LocalStorageKeys, S = T['state']>(
  store: T,
  storageKey: K,
  propName: keyof S,
  defaultValue?: LocalStorageKeys[K]
): Subscription<S> {
  const set = (key, value) => {
    if (key === propName) {
      setStoredValue(storageKey, value as any)
    }
  }
  const reset = () => {
    store.state[propName] = getStoredValue(storageKey, defaultValue)
  }
  reset()
  return { set, reset }
}
